import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProfile, useCreateWithdrawal, useWithdrawals, useLockedBalance, useInvestments, usePlans } from "@/lib/data-hooks";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, ArrowUpCircle, CheckCircle2 } from "lucide-react";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/withdraw")({
  head: () => ({ meta: [{ title: "Withdraw — CryptoBull Pro" }] }),
  component: Withdraw,
});

function Withdraw() {
  const { data: profile } = useProfile();
  const { data: withdrawals = [] } = useWithdrawals();
  const { data: locked = 0 } = useLockedBalance();
  const { data: investments = [] } = useInvestments();
  const { data: plans = [] } = usePlans();
  const [amount, setAmount] = useState("");
  const [addr, setAddr] = useState(profile?.btc_address ?? "");
  const create = useCreateWithdrawal();
  const available = Math.max(0, Number(profile?.balance ?? 0) - Number(locked ?? 0));

  const sortOrders = investments
    .map((i) => i.plan?.sort_order)
    .filter((n): n is number => typeof n === "number");
  const firstSort = sortOrders.length ? Math.min(...sortOrders) : null;
  const maxSort = sortOrders.length ? Math.max(...sortOrders) : null;
  const upgradeRequired = firstSort !== null && maxSort !== null && maxSort <= firstSort;
  const withdrawalsUnlocked = firstSort !== null && maxSort !== null && maxSort > firstSort;
  const nextPlan = upgradeRequired
    ? [...plans].sort((a, b) => a.sort_order - b.sort_order).find((p) => p.sort_order > (firstSort ?? 0))
    : null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">Withdraw funds</h1>
        <p className="text-sm text-muted-foreground">Available to withdraw: <span className="font-mono text-foreground">{fmtMoney(available)}</span></p>
      </div>

      {upgradeRequired && (
        <div className="glass rounded-2xl p-5 ring-1 ring-amber-500/40 flex items-start gap-3">
          <ArrowUpCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium">Upgrade to unlock withdrawals</div>
            <p className="text-xs text-muted-foreground mt-1">
              You've completed your first investment. To qualify for withdrawals, please make an investment in the
              {" "}<span className="text-foreground font-medium">{nextPlan?.name ?? "next higher"}</span> plan.
            </p>
            <Button asChild size="sm" variant="outline" className="mt-3">
              <Link to="/deposit" search={nextPlan ? { plan: nextPlan.slug } : {}}>Upgrade now</Link>
            </Button>
          </div>
        </div>
      )}

      {withdrawalsUnlocked && (
        <div className="glass rounded-2xl p-5 ring-1 ring-emerald-500/40 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium">Withdrawals unlocked</div>
            <p className="text-xs text-muted-foreground mt-1">
              You've upgraded to a higher-tier plan and your account is eligible to withdraw available funds.
            </p>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="glass rounded-2xl p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total balance</div>
          <div className="mt-1 font-mono text-xl">{fmtMoney(profile?.balance)}</div>
        </div>
        <div className="glass rounded-2xl p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</div>
          <div className="mt-1 font-mono text-xl">{fmtMoney(locked)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">In active plans & bots until they mature</div>
        </div>
        <div className="glass rounded-2xl p-4 ring-1 ring-primary/30">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Available</div>
          <div className="mt-1 font-mono text-xl text-primary">{fmtMoney(available)}</div>
        </div>
      </div>

      <form className="glass rounded-2xl p-6 space-y-4" onSubmit={(e) => {
        e.preventDefault();
        const amt = Number(amount);
        if (!amt || amt < 1) return toast.error("Invalid amount");
        if (!addr) return toast.error("Enter a BTC address");
        if (upgradeRequired) return toast.error(`Upgrade to the ${nextPlan?.name ?? "next"} plan to unlock withdrawals`);
        if (amt > available) return toast.error(`Only ${fmtMoney(available)} is available — the rest is locked in active plans/bots`);
        create.mutate({ amount: amt, btc_address: addr });
        setAmount("");
      }}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Amount (USD)</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>BTC address</Label>
            <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="bc1q…" />
          </div>
        </div>
        <Button type="submit" disabled={create.isPending}>{create.isPending ? "Submitting…" : "Request withdrawal"}</Button>
      </form>

      <div className="glass rounded-2xl p-6">
        <div className="text-sm font-medium mb-4">Withdrawal history</div>
        {withdrawals.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No withdrawals yet</div> : (
          <div className="space-y-2">
            {withdrawals.map((w) => (
              <div key={w.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-white/5">
                <div>
                  <div className="text-sm font-mono">{fmtMoney(w.amount)}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(w.created_at)} · {w.btc_address.slice(0,12)}…</div>
                </div>
                <Badge variant="outline" className="capitalize">{w.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}